import { BaseAPI } from "@utils/base-api";
import {
  SUBSCRIPTIONS_PUBLIC_GET_FRESH_PRICING_INFO_PATH,
} from "@./types/subscriptions/endpoint-paths";
import { PouchSize } from "@./cat-calculations";

import { PricingPlan } from "@/primary-models";

import {
  IFreshPricingPlan,
  TFreshMealPlanPricingMap,
  ITraySize,
} from "./types/meal-plan.types";

export class SubscriptionsAPI extends BaseAPI {

  /**
   * @param includeTrialPlans Whether to include trial plans in the response.
   * @returns A mapping of pricing plans to their plan pricing details.
   */
  public async getFreshMealPlanPricingInfo({
    includeTrialPlans = false,
  }: {
    includeTrialPlans?: boolean,
  }): Promise<TFreshMealPlanPricingMap> {
    const { data } = await this.axios.get(`${SUBSCRIPTIONS_PUBLIC_GET_FRESH_PRICING_INFO_PATH}`, {
      params: {
        all: includeTrialPlans,
      },
    });

    return data.pricingPlans;
  }

  /**
   * @param includeTrialPlans Whether to include trial plans in the response.
   * @returns An array of meal plans with their price information.
   */
  public async getAvailableMealPlans({
    includeTrialPlans = false,
  }: {
    includeTrialPlans?: boolean,
  }): Promise<IFreshPricingPlan[]> {
    const pricingPlans = await this.getFreshMealPlanPricingInfo({
      includeTrialPlans,
    });

    return Object.entries(pricingPlans).map(([ pricingPlan, pricingDetails ]) => ({
      ...pricingDetails,
      pricingPlan: pricingPlan as PricingPlan,
    }));
  }

  /**
   * @param pricingPlan The pricing plan to get the tray sizes for.
   * @returns A list of tray sizes with their price information.
   */
  public async getAvailableTraySizes(pricingPlan: PricingPlan): Promise<ITraySize[]> {
    const pricingPlans = await this.getFreshMealPlanPricingInfo({
      includeTrialPlans: pricingPlan === PricingPlan.TRIAL,
    });

    return Object.entries(pricingPlans[pricingPlan].traySize).map(([ traySize, traySizeDetails ]) => ({
      traySize: traySize as PouchSize,
      ...traySizeDetails,
    }));
  }

}
